<template>
  <div
    class="navbar-notifications"
    :style="{ top: top, right: right, bottom: bottom, left: left }"
  >
    <v-card width="415px" :height="notificationsCardHeight" class="notifications-card">
      <v-toolbar class="d-flex" height="60" id="navbar-notifications-header">
        <v-toolbar-title class="notifications-card-title">
          {{ $t("Notifications") }}
        </v-toolbar-title>
      </v-toolbar>
      <div class="menu-position" v-if="orderedMessages.length > 0">
        <notifications-menu
          :viewAll="true"
          :allRead="allRead"
          @readAllNotifications="readUnreadAllNotifications"
          @deleteAllNotifications="deleteAllNotifications"
        />
      </div>

      <v-list
        three-line
        :height="notificationsSectionHeight"
        :class="{
          scroll: viewAll,
          'no-messages': orderedMessages.length === 0
        }"
      >
        <span v-show="orderedMessages.length === 0">No Notifications</span>
        <template v-for="(notification, index) of orderedMessages">
          <v-list-item
            :key="notification.id"
            :class="{ read: notification.readAt }"
            id="navbar-notifications-list"
          >
            <v-list-item-avatar height="50px" width="50px" tile>
              <v-img
                v-if="notification.topic !== 'system'"
                :src="getImageUrl(notification.body.createdBy)"
              />
              <v-img v-else :src="getImageUrl(notification.userId)" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-subtitle class="text-left">
                <span class="text--primary">
                  {{ getUserName(notification.body.createdBy) }}
                </span>
                <span class="text--secundary">
                  {{ getNotificationMessage(notification.body.type) }}
                </span>

                <div
                  v-if="notification.topic === 'system'"
                  class="d-flex flex-column align-content-space-between"
                >
                  <a
                    :href="notification.body.url"
                    target="_blank"
                    class="text--secundary link"
                    >{{ notification.body.message }}</a
                  >
                </div>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action :key="index">
              <notifications-menu
                :notification="notification"
                :viewAll="false"
                @redirectToTask="redirectToTask($event)"
                @readNotification="readNotification($event)"
                @deleteNotification="deleteNotification($event)"
                @unReadNotification="unReadNotification($event)"
              />
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
      <view-all-button
        :socketMessage="socketMessage"
        @viewAll="viewAll = !viewAll"
        :viewAll="viewAll"
      />
    </v-card>
  </div>
</template>

<script>
import DynamicDateFromNow from "@/components/_Molecules/DynamicDateFromNow";
import { activitiesTasksDataMixin } from "@/mixins/field/activitiesTasksDataMixin";
import NotificationsMenu from "./_Organisms/NotificationsMenu.vue";
import ViewAllButton from "./_Atoms/ViewAllButton.vue";
import io from "socket.io-client";
import endpoints from "@/utils/endpoints";


export default {
  name: "notifications-window",
  props: {
    top: {
      type: String
    },
    right: {
      type: String
    },
    left: {
      type: String
    },
    bottom: {
      type: String
    },
    arrowLeft: {
      type: Boolean
    },
    notificationOpen: {
      type: Boolean
    }
  },
  components: { DynamicDateFromNow, NotificationsMenu, ViewAllButton },

  mixins: [activitiesTasksDataMixin],

  data() {
    return {
      userId: localStorage.getItem("user_id"),
      socketMessage: [],
      viewAll: false,
      socket: undefined
    };
  },

  watch: {
    /**
     * When notifications window is open, empty array is emited to clear badge number, when its closed, after user marks as read or
     * unread the badge number appears or not, updated according to the read notifications.
     */
    notificationOpen(value) {
      if (value) {
        this.$emit("notifications", []);
      } else {
        this.$emit("notifications", this.socketMessage);
      }
    }
  },

  computed: {
    /**
     * if all notifications are marked as read allRead is sent to child component NotificationReadAllMenu to change the text
     * that is presented to the user and the action.
     */
    allRead() {
      return this.socketMessage.every(item => item.readAt);
    },
    /**
     * sorts array so the newest is shown on top of the list.
     * restricts the user to only see 4 notifications, if user clicks view all, all notifications are shown.
     */
    orderedMessages() {
      let messages = this.socketMessage
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (!this.viewAll) {
        let viewRestricted = messages.slice(0, 4);
        return viewRestricted;
      }
      return messages;
    },

    /**
     * Returns the height of the notifications section, according to the number of notifications
     */
    notificationsSectionHeight() {
      return this.orderedMessages.length === 0 ? "30px" : "364px";
    },

    /**
     * Returns the height of the notifications card, according to the number of notifications
     */
    notificationsCardHeight() {
      return this.orderedMessages.length === 0 ? "113px" : "464px";
    }
  },

  created() {
    /**
     * initiates socket instance
     */
    if(sessionStorage.getItem("is_bidding") === 'true')
      return;

    this.socket = io(endpoints.WS_URL, {
      extraHeaders: {
        Authorization: "Bearer " + localStorage.getItem("auth_token")
      }
    });

    /**
     * initial request (similar to get) gets all available notifications.
     * Emits to Hub component the initial array.
     */
    this.socket.emit(
      "notifications.get",
      { userId: localStorage.getItem("user_id") },
      data => {
        this.socketMessage = data;
        this.$emit("notifications", this.socketMessage);
      }
    );

    /**
     * Opens event listening channel, every time a new notification is received it gets pushed to socketMessage array.
     * Emits to Hub component the updated array with a new message.
     */
    this.socket.on("notifications.notify", data => {
      this.socketMessage.push(data);
      this.$emit("notifications", this.socketMessage);
    });

    /**
     * loads user list
     */
    this.loadAllUsers();
  },
  /**
   * when component is destroyed disconnects from socket
   */
  destroyed() {
    if(this.socket)
      this.socket.disconnect();
  },

  methods: {
    /**
     * @param {String} - uuid of clicked notification
     * emits to the websocket an event, the websocket sends back the clicked notification with the readAt property as null.
     * the function also updates the socketMessage array and replaces the clicked item with the one received.
     * Emits to Hub component the updated array.
     */
    unReadNotification(id) {
      this.socket.emit("notifications.unread", { id: id }, data => {
        this.findIndexOfNotifications(data);
      });
    },

    /**
     * @param {String} - uuid of clicked notification
     * emits to the websocket an event, the websocket sends back the clicked notification with the readAt property updated.
     * the function also updates the socketMessage array and replaces the clicked item with the one received.
     */
    readNotification(id) {
      this.socket.emit("notifications.read", { id: id }, data => {
        this.findIndexOfNotifications(data);
      });
    },

    /**
     * @param {String} - receives 'read' value, and marks all notifications as read else marks as unread
     */
    readUnreadAllNotifications(val) {
      if (val === "read") {
        this.socketMessage.forEach(item => {
          this.socket.emit("notifications.read", { id: item.id }, data => {
            this.findIndexOfNotifications(data);
          });
        });
      } else {
        this.socketMessage.forEach(item => {
          this.socket.emit("notifications.unread", { id: item.id }, data => {
            this.findIndexOfNotifications(data);
          });
        });
      }
    },

    /**
     * deletes all notifications
     */
    deleteAllNotifications() {
      this.socketMessage.forEach(item => {
        this.socket.emit("notifications.delete", { id: item.id });
      });
      this.socketMessage = [];
      this.$emit("notifications", this.socketMessage);
    },

    /**
     * @param {String} Uuid - uuid of clicked notification
     * Deletes the targeted notification from the DB and from socketMessage array.
     */
    deleteNotification(id) {
      this.socket.emit("notifications.delete", { id: id });
      let index = this.socketMessage.findIndex(item => item.id === id);
      this.socketMessage.splice(index, 1);
      this.$emit("notifications", this.socketMessage, index);
    },

    /**
     * @param {String} uuid - uuid of task from clicked notification
     * @param {String} activityUUID - uuid of activity where the task is associated
     * Redirects the user to the task view page.
     */
    redirectToTask(uuid, activityUUID) {
      this.$router.push({
        name: "field-management-task",
        params: { uuid: activityUUID, uuidTask: uuid }
      });
    },

    /**
     * @param {Object} data - receives notification from emit event
     * finds index of notification whenever needed
     */
    findIndexOfNotifications(data) {
      let index = this.socketMessage.findIndex(item => item.id === data.id);
      this.socketMessage.splice(index, 1, data);
    },

    /**
     * @param type - type of notification
     * @returns - Message according to type of notification
     */
    getNotificationMessage(type) {
      let phrase;
      switch (type) {
        case "created":
          phrase = this.$i18n.t("CreatedTaskNotification");
          break;
        case "scheduled":
          phrase = this.$i18n.t("ScheduledTaskNotification");
          break;
        case "pending_approval":
          phrase = this.$i18n.t("PendingApprovalTaskNotification");
          break;
        case "in_progress":
          phrase = this.$i18n.t("InProgressTaskNotification");
          break;
        case "company_assign":
          phrase = this.$i18n.t("TaskNeedsAssignment");
          break;
        case "blocked":
          phrase = this.$i18n.t("BlockTaskNotification");
          break;
        default:
          "No Message";
          break;
      }
      return phrase;
    }
  }
};
</script>

<style scoped>
.notifications-card-title {
  color: #231E46;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px; /* 100% */
  text-transform: uppercase;
}

.logo {
  width: 60px;
}
.scroll {
  overflow: scroll;
}

.button-border {
  border-end-start-radius: 2px !important;
  border-end-end-radius: 2px !important;
}
.content {
  display: flex;
  justify-content: flex-start;
  width: 100%;
}
.read {
  background-color: rgba(39, 39, 39, 0.075);
}

.no-messages {
  margin-top: 0;
}

.notifications-card {
  border-radius: 8px;
}

#navbar-notifications-list >>> .v-avatar.v-list-item__avatar.v-avatar--tile {
  border-radius: 8px !important;
}

#navbar-notifications-list {
  border-top: 1px rgba(35, 30, 70, 0.50) solid;
}
</style>



// WEBPACK FOOTER //
// src/components/Management/Notifications/NotificationsWindow.vue